import React, { useState, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import Icon from '../../../components/AppIcon';

const StuckProcessingPanel = ({ compact = false, limit = 50, refreshTrigger }) => {
  const [stuckPayments, setStuckPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadStuckPayments();
  }, [refreshTrigger]);

  const loadStuckPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch pending webhook events older than 30 minutes
      const data = await adminService?.getWebhookEvents({
        limit,
        status: 'pending'
      });

      // Filter for events stuck in pending state (older than 30 minutes)
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
      const stuckEvents = data?.filter((event) => {
        const eventDate = new Date(event?.createdAt);
        return eventDate < thirtyMinutesAgo;
      });

      setStuckPayments(stuckEvents);
    } catch (err) {
      console.error('Load stuck payments error:', err);
      setError(err?.message || 'Failed to load stuck payments');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const calculateDuration = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffHours > 0) {
      return `${diffHours}h ${diffMins % 60}m`;
    }
    return `${diffMins}m`;
  };

  const getDurationSeverity = (createdAt) => {
    const diffMs = new Date() - new Date(createdAt);
    const diffHours = diffMs / 3600000;

    if (diffHours > 2) return { color: 'text-destructive', bgColor: 'bg-destructive/10', label: 'Critical' };
    if (diffHours > 1) return { color: 'text-warning', bgColor: 'bg-warning/10', label: 'Warning' };
    return { color: 'text-muted-foreground', bgColor: 'bg-muted', label: 'Monitoring' };
  };

  const extractCustomerInfo = (payload) => {
    return {
      email: payload?.customer_email || payload?.customer_details?.email || 'N/A',
      customerId: payload?.customer || 'N/A',
      subscriptionId: payload?.subscription || 'N/A'
    };
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="Clock" size={20} className="text-warning" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Stuck Processing States</h2>
            <p className="text-sm text-muted-foreground">Payments pending for more than 30 minutes</p>
          </div>
        </div>
        {!compact && (
          <button
            onClick={loadStuckPayments}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {stuckPayments?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
            <p className="text-muted-foreground">No stuck payments found</p>
            <p className="text-sm text-muted-foreground mt-1">All payments are processing normally</p>
          </div>
        ) : (
          stuckPayments?.map((payment) => {
            const customerInfo = extractCustomerInfo(payment?.payload);
            const duration = calculateDuration(payment?.createdAt);
            const severity = getDurationSeverity(payment?.createdAt);
            const isExpanded = expandedId === payment?.id;

            return (
              <div
                key={payment?.id}
                className="border border-warning/20 rounded-lg p-4 hover:bg-warning/5 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-foreground">{payment?.eventType}</span>
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${severity?.bgColor}`}>
                        <Icon name="Clock" size={12} className={severity?.color} />
                        <span className={`text-xs font-medium ${severity?.color}`}>{severity?.label}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Customer: </span>
                        <span className="text-foreground font-medium">{customerInfo?.email}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Duration: </span>
                        <span className={`font-medium ${severity?.color}`}>{duration}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Event ID: </span>
                        <span className="text-foreground font-mono text-xs">{payment?.eventId}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Subscription: </span>
                        <span className="text-foreground font-mono text-xs">{customerInfo?.subscriptionId}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Started: {adminService?.formatRelativeTime(payment?.createdAt)}</span>
                      <span>{adminService?.formatDate(payment?.createdAt)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleExpand(payment?.id)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0"
                  >
                    <Icon
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      size={16}
                      className="text-muted-foreground"
                    />
                  </button>
                </div>

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="mb-4">
                      <p className="text-sm font-medium text-foreground mb-2">Troubleshooting Actions:</p>
                      <div className="flex flex-wrap gap-2">
                        <button className="px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors">
                          Retry Processing
                        </button>
                        <button className="px-3 py-1.5 text-xs bg-muted text-foreground rounded hover:bg-muted/80 transition-colors">
                          Mark as Failed
                        </button>
                        <button className="px-3 py-1.5 text-xs bg-muted text-foreground rounded hover:bg-muted/80 transition-colors">
                          View in Stripe
                        </button>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-foreground mb-2">Full Event Payload:</p>
                    <div className="bg-muted/50 rounded p-3 overflow-x-auto">
                      <pre className="text-xs font-mono text-foreground">
                        {JSON?.stringify(payment?.payload, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StuckProcessingPanel;